import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { TextField, Alert, Stack } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { MIconButton } from 'src/components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack5';
// misc
import { INITIAL_PATH } from '../../../constants';
import { PATH_AUTH } from 'src/routes/paths';
import { AVAILABLE_MFA_METHODS } from 'src/@types/authentication';
import { PW_MATCHES, PW_MAX, PW_MIN } from 'src/validation/password';
// types
import type { Dispatch, SetStateAction } from 'react';
import type { SignInResult } from 'src/contexts/AwsCognitoContext';

type InitialValues = {
  password: string;
  reenterPassword: string;
  afterSubmit?: string;
};

// MFA対応 start --------------
type UpdatePasswordFormProps = {
  setIsVerifyMfaCode: Dispatch<SetStateAction<boolean>>;
  setActiveMfaMethod: Dispatch<SetStateAction<AVAILABLE_MFA_METHODS | null>>;
  isRequiredMfa: boolean;
};
// MFA対応 end --------------

// ----------------------------------------------------------------------

export default function UpdatePasswordForm({ setIsVerifyMfaCode, setActiveMfaMethod, isRequiredMfa }: UpdatePasswordFormProps) {
  const { updatePassword } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const UpdateSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('message.pwRequired'))
      .max(PW_MAX, t('message.pwMax'))
      .min(PW_MIN, t('message.pwMin'))
      .matches(PW_MATCHES, t('message.pwMatch')),
    reenterPassword: Yup.string().oneOf([Yup.ref('password'), null], t('message.newPwErr')),
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      password: '',
      reenterPassword: '',
    },
    validationSchema: UpdateSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      if (values.password != values.reenterPassword) {
        setErrors({ afterSubmit: 'New password and reenter new password do not match.' });
        setSubmitting(false);
        return
      }
      try {
        // MFA対応 start --------------
        // パスワード変更
        const result: SignInResult = await updatePassword(values.password);

        // 完了メッセージを表示
        enqueueSnackbar(t('updatePassword.complete'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });

        // MFA必須ユーザーの場合
        if (isRequiredMfa) {
          // すでにMFA方法設定済みの場合、MFA認証コード入力画面へ遷移
          if (result.isRequiredMfa) {
            if (!result.mfaMethod) throw new Error("Cannot find User's Mfa Method.");
            // MFA認証コード入力画面を表示
            setIsVerifyMfaCode(true);
            setActiveMfaMethod(result.mfaMethod); // ユーザー選択済みのMFA方式
            return;
          }
          // MFA方法未設定の場合、MFA方式選択画面へ遷移
          navigate(PATH_AUTH.selectMfaMethod, { replace: true });
          return;
        }

        if (isMountedRef.current) {
          setSubmitting(false);
          // HOME画面へ遷移
          window.location.pathname !== '/'
            ? navigate(window.location.pathname + window.location.search, { replace: true })
            : navigate(INITIAL_PATH, { replace: true });
        }
        // MFA対応 end --------------
      } catch (error: any) {
        if (isMountedRef.current) {
          setErrors({ afterSubmit: error.message });
          setSubmitting(false);
        }
      }
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            {...getFieldProps('password')}
            type='password'
            label={t('updatePassword.password')}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
          <TextField
            fullWidth
            {...getFieldProps('reenterPassword')}
            type='password'
            label={t('updatePassword.password-reenter')}
            error={Boolean(touched.reenterPassword && errors.reenterPassword)}
            helperText={touched.reenterPassword && errors.reenterPassword}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {t('button.save')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
